/**
 * Journal d'audit (super_admin uniquement). Liste paginée et filtrable de
 * toutes les actions sensibles tracées par le back. Présentation lisible :
 * acteur, action (badge coloré par préfixe), entité, preview JSON du payload.
 */
import type {
  AuditAction,
  AuditActionEntityType,
  AuditActionType,
  ListAuditActionsQuery,
} from '@bcl/shared';
import {
  auditActionEntityTypeSchema,
  auditActionTypeSchema,
} from '@bcl/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { listAuditActions } from '@/features/admin/audit-api';
import { extractErrorMessage } from '@/features/auth/error-message';

const PAGE_SIZE = 25;

// Dérivés des schémas partagés : le journal couvre automatiquement toutes les
// entités et actions tracées (staff, projets, clients, documents, paiements,
// packs, rendez-vous, comptes rendus, partenaires, leads, interventions,
// factures sous-traitant…) sans risque de filtre obsolète.
const entityTypes: AuditActionEntityType[] = [...auditActionEntityTypeSchema.options];

const actionTypes: AuditActionType[] = [...auditActionTypeSchema.options];

export function actionBadgeClass(action: AuditActionType): string {
  const prefix = action.split('.')[0];
  switch (prefix) {
    case 'staff':
      return 'bg-purple-100 text-purple-800';
    case 'project':
      return 'bg-blue-100 text-blue-800';
    case 'client':
      return 'bg-amber-100 text-amber-800';
    case 'document':
      return 'bg-emerald-100 text-emerald-800';
    case 'payment':
      return 'bg-rose-100 text-rose-800';
    case 'pack':
      return 'bg-indigo-100 text-indigo-800';
    case 'appointment':
      return 'bg-sky-100 text-sky-800';
    case 'report':
      return 'bg-teal-100 text-teal-800';
    case 'partner':
      return 'bg-orange-100 text-orange-800';
    case 'partner_invoice':
      return 'bg-lime-100 text-lime-800';
    case 'lead':
      return 'bg-cyan-100 text-cyan-800';
    case 'intervention':
      return 'bg-fuchsia-100 text-fuchsia-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

export function formatPayloadPreview(payload: Record<string, unknown>): string {
  const json = JSON.stringify(payload);
  return json.length > 80 ? `${json.slice(0, 80)}…` : json;
}

export function formatEntityRef(
  entityType: AuditActionEntityType,
  entityId: string | null,
): string {
  if (!entityId) return entityType;
  return `${entityType} · ${entityId.slice(0, 8)}…`;
}

type Filters = {
  from: string;
  to: string;
  actorId: string;
  entityType: '' | AuditActionEntityType;
  action: '' | AuditActionType;
};

const emptyFilters: Filters = {
  from: '',
  to: '',
  actorId: '',
  entityType: '',
  action: '',
};

function toQuery(filters: Filters, page: number): ListAuditActionsQuery {
  const q: ListAuditActionsQuery = {
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };
  if (filters.from) q.from = new Date(filters.from).toISOString();
  if (filters.to) q.to = new Date(filters.to).toISOString();
  if (filters.actorId) q.actorId = filters.actorId;
  if (filters.entityType) q.entityType = filters.entityType;
  if (filters.action) q.action = filters.action;
  return q;
}

export function AdminAuditPage() {
  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [page, setPage] = useState(0);

  const query = useQuery<AuditAction[]>({
    queryKey: ['admin', 'audit', filters, page],
    queryFn: () => listAuditActions(toQuery(filters, page)),
  });

  if (query.error) {
    toast.error(extractErrorMessage(query.error));
  }

  function resetFilters() {
    setFilters(emptyFilters);
    setPage(0);
  }

  const rows = query.data ?? [];
  const hasNext = rows.length === PAGE_SIZE;

  return (
    <DashboardLayout variant="admin" title="Journal d'audit">
      <Card>
        <CardHeader>
          <CardTitle>Actions sensibles</CardTitle>
          <CardDescription>
            Traçabilité des décisions back-office : créations / suppressions
            de comptes, changements de statut, validations, etc.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-1.5">
              <Label htmlFor="audit-from">Du</Label>
              <Input
                id="audit-from"
                type="date"
                value={filters.from}
                onChange={(e) => {
                  setFilters({ ...filters, from: e.target.value });
                  setPage(0);
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="audit-to">Au</Label>
              <Input
                id="audit-to"
                type="date"
                value={filters.to}
                onChange={(e) => {
                  setFilters({ ...filters, to: e.target.value });
                  setPage(0);
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="audit-entity">Entité</Label>
              <select
                id="audit-entity"
                value={filters.entityType}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    entityType: e.target.value as Filters['entityType'],
                  });
                  setPage(0);
                }}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Tous</option>
                {entityTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="audit-action">Action</Label>
              <select
                id="audit-action"
                value={filters.action}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    action: e.target.value as Filters['action'],
                  });
                  setPage(0);
                }}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Toutes</option>
                {actionTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="audit-actor">Acteur (UUID)</Label>
              <Input
                id="audit-actor"
                placeholder="00000000-…"
                value={filters.actorId}
                onChange={(e) => {
                  setFilters({ ...filters, actorId: e.target.value });
                  setPage(0);
                }}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={resetFilters}>
              Réinitialiser filtres
            </Button>
            <p className="text-sm text-gray-500">
              {query.isLoading ? 'Chargement…' : `${rows.length} entrées`}
            </p>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Acteur</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Entité</TableHead>
                <TableHead>Payload</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-sm text-gray-500"
                  >
                    {query.isLoading ? 'Chargement…' : 'Aucune entrée.'}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap text-xs text-gray-600">
                      {new Date(row.createdAt).toLocaleString('fr-FR')}
                    </TableCell>
                    <TableCell>
                      {row.actorName ? (
                        <div className="flex flex-col">
                          <span className="font-medium">{row.actorName}</span>
                          <span className="text-xs text-gray-500">
                            {row.actorEmail}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs italic text-gray-400">
                          Système / supprimé
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={actionBadgeClass(row.action)}>
                        {row.action}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {formatEntityRef(row.entityType, row.entityId)}
                    </TableCell>
                    <TableCell
                      className="max-w-xs truncate font-mono text-xs text-gray-600"
                      title={JSON.stringify(row.payload, null, 2)}
                    >
                      {formatPayloadPreview(row.payload)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Précédent
            </Button>
            <span className="text-sm text-gray-500">Page {page + 1}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasNext}
              onClick={() => setPage((p) => p + 1)}
            >
              Suivant
            </Button>
          </div>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
